<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useGameContest } from '@/store'
import { clientApi } from '@/api/config'

import { useScrollTo } from '@/Old/compose/useScrollTo'
import { useLocalStorageAvailability } from '@/composables/localStorage'

import GameContestNotFound from '@/views/GameContestNotFound.vue'

import Animation from '@/Old/components/Animation.vue'
import ErrorText from '@/components/ErrorText.vue'
import ErrorModal from '@/Old/components/ErrorModal.vue'
import Hero from '@/Old/components/Hero.vue'
import LoseModal from '@/Old/components/LoseModal.vue'
import SubmitForm from '@/Old/components/SubmitForm.vue'
import TrackModal from '@/Old/components/TrackModal.vue'
import TheFooter from '@/components/TheFooter.vue'
import FortuneWheel from '@/components/ContestGame/Mechanics/FortuneWheel.vue'

import type { ParticipantParams } from '@/api/api'
import type {
  CountryStores,
  ContestColor,
  ContestGameReward,
  FormattedContestGameReward,
  GameContest,
  Merchant,
} from '@/types'

const props = defineProps<{
  id: string
  storeSlug: string
  contestGame: GameContest
  contestColor: ContestColor
  contestReward: ContestGameReward | null
  merchant: Merchant
  contestGameRewards: FormattedContestGameReward[]
  logo: string
  locale: string
}>()

const route = useRoute()
const store = useGameContest()
const { setContestReward } = store

const tokenIsInvalid = ref(false)
const isNotFound = ref(false)
const animation = ref<Animation | null>(null)
const backendError = ref('')
const contestGameErrorModal = ref(null)
const contestGameModal = ref(false)
const hasSeenTrackingModal = ref(false)
const initAnimation = ref(false)
const isSubmit = ref(false)
const lose = ref(false)
const loseModal = ref(false)
const trackingModal = ref(false)
const availableCountries = ref<CountryStores[]>([])
const phoneNumber = ref('')

const webAppUrl = computed(() => {
  let url = `https://${props.merchant.webAppUrl}`
  if (phoneNumber.value) {
    url += `?phoneNumber=${encodeURIComponent(phoneNumber.value)}`
  }
  return url
})

const focusInput = () => {
  backendError.value = ''
}

const done = () => {
  if (!lose.value) {
    animation.value?.render()
  } else {
    loseModal.value = true
  }
}

const showTrackModal = () => {
  if (!props.contestGame?.stores?.[0]?.storeId || !props.contestGame?.contestGameBoostType?.slug)
    return

  const localStorageKey = `hasParticipated-${props.contestGame.id}-${props.contestGame.stores[0].storeId}-${props.contestGame.contestGameBoostType.slug}`
  const hasSeen =
    localStorage.getItem(localStorageKey) ||
    localStorage.getItem(`hasParticipated-${props.contestGame.id}`)

  if (
    props.contestGame.contestGameBoostType &&
    props.contestGame.contestGameBoostType.slug === 'none'
  )
    return
  if (hasSeen === 'true') return

  if (!hasSeenTrackingModal.value) {
    trackingModal.value = true
  }
}

const firstnameInputFocus = ref(false)
const scrollToForm = () => {
  useScrollTo('form', 800)
  firstnameInputFocus.value = true
}

const submit = async (payload: ParticipantParams) => {
  isSubmit.value = true

  await clientApi.client.contestGamesParticipants
    .create(props.id, props.storeSlug, {
      ...payload,
      origin: route.query.origin || null,
    })
    .then((resContestGame: { data: { contestGameReward: ContestGameReward } }) => {
      useScrollTo('wheel', 800)

      if (resContestGame.data.contestGameReward) {
        lose.value = false
        setContestReward(resContestGame.data.contestGameReward)
        phoneNumber.value = payload.phoneNumber
      } else {
        lose.value = true
        const nullReward = props.contestGameRewards.find((x) => !x.type)

        if (nullReward) {
          setContestReward(nullReward)
        }
      }

      if (contestGameErrorModal.value === null) {
        if (useLocalStorageAvailability() === true) {
          localStorage.setItem(`hasParticipated-${props.contestGame.id}`, 'true')
          localStorage.setItem(
            `hasParticipated-${props.contestGame.id}-${props.contestGame.stores[0].storeId}-${props.contestGame.contestGameBoostType.slug}`,
            'true',
          )
        }

        isSubmit.value = false
      }
    })
    .catch((err) => {
      isSubmit.value = false
      if (err?.response?.status === 409 || err?.response?.status === 500) {
        contestGameErrorModal.value = err.response
        contestGameModal.value = true
      } else {
        if (err?.response?.data?.errors?.phone_number) {
          backendError.value = 'errors.invalidPhone'
        }
        if (err?.response?.data?.errors?.email) {
          backendError.value = 'errors.invalidEmail'
        }
      }
    })
}

const loadCountries = () => {
  const contest = props.contestGame
  const selectedStore = contest.stores.find((store) => store.store.slug === route.params.storeSlug)
  const hasAvailableCountries = contest.stores && selectedStore && selectedStore.store.countryStores
  if (hasAvailableCountries) {
    availableCountries.value = selectedStore.store.countryStores
  }
}

onMounted(() => {
  if (props.contestGame.id) {
    initAnimation.value = true
    if (props.contestGame.title) {
      document.title = props.contestGame.title
    }

    if (useLocalStorageAvailability() === true) {
      showTrackModal()
      loadCountries()
    } else {
      trackingModal.value = true
    }
  }
})

const closeModal = () => {
  firstnameInputFocus.value = true
  hasSeenTrackingModal.value = true
}
</script>

<template>
  <div class="bg-white">
    <GameContestNotFound v-if="isNotFound" />
    <ErrorText v-else-if="tokenIsInvalid" :text="$t('errors.invalidToken')"> 🤕 </ErrorText>
    <div
      v-else
      class="overflow-x-hidden"
      :style="{
        backgroundColor: `${contestColor.backgroundColor}`,
        color: `${contestColor.textColor}`,
      }"
    >
      <animation
        v-if="initAnimation"
        ref="animation"
        :contest-game="contestGame"
        :contest-color="contestColor"
        :gift="contestReward"
        :web-app-url="webAppUrl"
      />

      <LoseModal v-model="loseModal" :contest-game="contestGame" />

      <TrackModal
        v-model="trackingModal"
        :contest-game="contestGame"
        :store-slug="storeSlug"
        @has-seen-tracking-modal="closeModal"
      />

      <Hero
        :contest-color="contestColor"
        :contest-game="contestGame"
        @scroll-to-form="scrollToForm"
      />

      <div
        class="mx-auto flex max-w-6xl flex-col flex-wrap items-center justify-center md:flex-row md:justify-between"
      >
        <FortuneWheel
          :rewards="contestGameRewards"
          :color="contestColor"
          :contest-game="contestGame"
          :logo="logo"
          :merchant="merchant"
          :web-app-url="webAppUrl"
          @completed="done"
        />

        <div class="col -mt-6 px-8 pb-12 md:px-0">
          <h1 class="mb-2 text-center text-3xl font-bold md:text-left">
            {{ contestGame.formTitle }}
          </h1>
          <p class="mb-8 text-center text-xl md:text-left">
            {{ contestGame.formSubtitle }}
          </p>

          <error-modal
            v-model="contestGameModal"
            :error-text="contestGameErrorModal"
            :locale="locale"
          />
          <div class="mx-auto max-w-3xl">
            <SubmitForm
              ref="form"
              :contest-game="contestGame"
              :backend-error="backendError"
              :is-submit="isSubmit"
              :locale="locale"
              :conditions-url="contestGame.conditionsUrl"
              :available-countries="availableCountries"
              :firstname-input-focus="firstnameInputFocus"
              @submit="submit"
              @focus-input="focusInput"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <TheFooter medium="identification" :is-fixed="false" />
</template>

<style>
div .input-style {
  @apply px-6 py-4 border border-gray-200 w-full shadow-sm text-gray-700;
}
.wheel {
  @apply mx-auto;
}
.col {
  @apply w-full;
}
@screen md {
  .col {
    width: calc(100% / 2 - 5rem);
  }
}
.wheel {
  width: 100%;
  height: auto;
  margin: 0 auto;
}
.wheel textPath {
  letter-spacing: 1px;
  stroke: rgb(0 0 0 / 10%);
}
/* ----------- iPhone 6+, 7+ and 8+ Portrait ----------- */
@media screen and (min-device-width: 320px) and (max-device-width: 360px) and (orientation: portrait) {
  .wheelholder image {
    width: 80px;
    height: 80px;
    transform: translate(10px, 10px);
  }
  .wheel_font-size--1 textPath,
  .wheel_font-size--2 textPath,
  .wheel_font-size--3 textPath {
    font-size: 16px;
  }
  .wheel_font-size--4 textPath {
    font-size: 12px;
  }
  .wheel_font-size--5 textPath {
    font-size: 9px;
  }
  .wheel_font-size--6 textPath {
    font-size: 9px;
  }
  .wheel_font-size--7 textPath {
    font-size: 10px;
  }
  .wheel_font-size--8 textPath {
    font-size: 8px;
  }
}
@media screen and (min-device-width: 360px) and (max-device-width: 412px) and (orientation: portrait) {
  .wheel_font-size--1 textPath,
  .wheel_font-size--2 textPath,
  .wheel_font-size--3 textPath {
    font-size: 18px;
  }
  .wheel_font-size--4 textPath {
    font-size: 14px;
  }
  .wheel_font-size--5 textPath,
  .wheel_font-size--6 textPath,
  .wheel_font-size--7 textPath {
    font-size: 11px;
  }
  .wheel_font-size--8 textPath {
    font-size: 10px;
  }
}
@media screen and (min-device-width: 412px) and (max-device-width: 736px) and (orientation: portrait) {
  .wheel_font-size--1 textPath,
  .wheel_font-size--2 textPath,
  .wheel_font-size--3 textPath {
    font-size: 18px;
  }
  .wheel_font-size--4 textPath {
    font-size: 15px;
  }
  .wheel_font-size--5 textPath,
  .wheel_font-size--6 textPath,
  .wheel_font-size--7 textPath {
    font-size: 13px;
  }
  .wheel_font-size--8 textPath {
    font-size: 11px;
  }
}
</style>
